import type {ModalPhase} from './modal-utils';

import {useEffect, useId, useRef, useState} from 'react';

import {getFocusableElements, MODAL_ANIMATION_MS} from './modal-utils';

export function useAnimatedDialog(show: boolean, onClose?: () => void) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusedElement = useRef<HTMLElement | null>(null);
  const leaveTimerRef = useRef<number | null>(null);
  const titleId = useId();

  const [phase, setPhase] = useState<ModalPhase>(show ? 'preEnter' : 'hidden');

  const isMounted = phase !== 'hidden';
  const isPanelOpen = phase === 'enter' || phase === 'idle';
  const isInteractive = phase === 'idle';

  useEffect(() => {
    if (show) {
      if (leaveTimerRef.current != null) {
        window.clearTimeout(leaveTimerRef.current);
        leaveTimerRef.current = null;
      }

      setPhase((current) => {
        if (current === 'hidden' || current === 'leave') {
          return 'preEnter';
        }
        return current;
      });
      return;
    }

    setPhase((current) => {
      if (current === 'hidden') {
        return 'hidden';
      }
      return 'leave';
    });
  }, [show]);

  useEffect(() => {
    if (phase !== 'preEnter') {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      setPhase('enter');
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'leave') {
      return;
    }

    leaveTimerRef.current = window.setTimeout(() => {
      setPhase('hidden');
    }, MODAL_ANIMATION_MS);

    return () => {
      if (leaveTimerRef.current != null) {
        window.clearTimeout(leaveTimerRef.current);
        leaveTimerRef.current = null;
      }
    };
  }, [phase]);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    previousFocusedElement.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocusedElement.current?.focus?.();
    };
  }, [isMounted]);

  useEffect(() => {
    if (!isInteractive) {
      return;
    }

    const focusDialog = window.requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }

      const focusable = getFocusableElements(dialog);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        dialog.focus();
      }
    });

    return () => window.cancelAnimationFrame(focusDialog);
  }, [isInteractive]);

  useEffect(() => {
    if (!isInteractive) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }

      const focusable = getFocusableElements(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isInteractive, onClose]);

  const handlePanelTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') {
      return;
    }

    if (phase === 'enter') {
      setPhase('idle');
    }

    if (phase === 'leave') {
      if (leaveTimerRef.current != null) {
        window.clearTimeout(leaveTimerRef.current);
        leaveTimerRef.current = null;
      }
      setPhase('hidden');
    }
  };

  return {
    dialogRef,
    titleId,
    isMounted,
    isPanelOpen,
    isInteractive,
    handlePanelTransitionEnd
  };
}
